import React, { useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { I18n } from 'react-redux-i18n';
import { Button, Modal, Message } from 'semantic-ui-react';

import { useAppDispatch } from '../../hooks';
import { Record } from '../../modules/bin/utility';
import {
  getSpamActionKeys,
  getSpamModalContextKey,
  getSpamModalContextParams,
} from '../../pages/fundraiser/markSpamHelpers';

interface MarkSpamModalProps {
  entityId: number;
  entity: any;
  stateSliceKey: string;
  markAsSpam: (id: number) => any;
  hideConfirmation: () => any;
  getRecord: (id: number) => any;
  translationBase: string;
}

const MarkSpamModal: React.FC<MarkSpamModalProps> = ({
  entityId,
  entity,
  stateSliceKey,
  markAsSpam,
  hideConfirmation,
  getRecord,
  translationBase,
}) => {
  const dispatch = useAppDispatch();
  const { markSpamConfirmation, saveRecord } = useSelector(
    (state: any) => state[stateSliceKey]
  );

  const isLoading = Record.isRecordLoading(saveRecord);
  const isError = saveRecord?.error?.show === true;
  const errorKeys: string[] | null = saveRecord?.error?.errorKeys ?? null;
  const wasLoading = useRef(false);

  const actionKeys = getSpamActionKeys(entity);
  const contextKey = getSpamModalContextKey(entity);
  const contextParams = getSpamModalContextParams(entity);

  useEffect(() => {
    if (isLoading) {
      wasLoading.current = true;
    }
    if (wasLoading.current && isError) {
      wasLoading.current = false;
    }
    if (wasLoading.current && !isLoading && !isError) {
      wasLoading.current = false;
      dispatch(hideConfirmation());
      dispatch(getRecord(entityId));
    }
  }, [isLoading, isError, dispatch, entityId, hideConfirmation, getRecord]);

  const handleConfirm = () => {
    dispatch(markAsSpam(entityId));
  };

  const handleClose = () => {
    dispatch(hideConfirmation());
  };

  return (
    <Modal
      className="wizard"
      size="small"
      dimmer="inverted"
      closeIcon
      open={markSpamConfirmation}
      onClose={handleClose}
    >
      <Modal.Header>
        <h3>{I18n.t(`${translationBase}.mark-spam-modal.title`)}</h3>
      </Modal.Header>
      <Modal.Content>
        {isError && (
          <Message negative>
            {errorKeys?.length
              ? errorKeys.map((key: string) => {
                  const shortKey = key.includes('.') ? key.split('.').pop() : key;
                  return <p key={key}>{I18n.t(`error-messages.mark-spam.${shortKey}`)}</p>;
                })
              : <p>{I18n.t('general.general-error-message')}</p>
            }
          </Message>
        )}
        <p>{I18n.t(contextKey, contextParams)}</p>
        <p><strong>{I18n.t(`${translationBase}.mark-spam-modal.actions-heading`)}</strong></p>
        <ul>
          {actionKeys.map((key) => (
            <li key={key}>{I18n.t(key)}</li>
          ))}
        </ul>
        <p style={{ color: 'var(--color-error, #d32f2f)', fontWeight: 600 }}>
          {I18n.t(`${translationBase}.mark-spam-modal.warning`)}
        </p>
        <div className="button-wrapper align-right" style={{ marginTop: '16px' }}>
          <Button onClick={handleClose}>
            {I18n.t(`${translationBase}.mark-spam-modal.cancel`)}
          </Button>
          <Button
            negative
            onClick={handleConfirm}
            loading={isLoading}
            disabled={isLoading}
          >
            {I18n.t(`${translationBase}.mark-spam-modal.confirm`)}
          </Button>
        </div>
      </Modal.Content>
    </Modal>
  );
};

export default MarkSpamModal;
